// src/pages/VerifyEmail.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { verifyEmail, getProfile, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Mail,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Shield,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { authService } from "../services/api";

const VerifyEmail: React.FC = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const { isLoading, error, user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );

  const email = location.state?.email || user?.email;

  useEffect(() => {
    if (!email) {
      navigate("/register");
    }
  }, [email, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (isAuthenticated && user?.email_verified_at) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Please enter all 6 digits");
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      toast.error("Please enter valid digits (0-9)");
      return;
    }

    try {
      const result = await dispatch(verifyEmail(otpString) as any);

      if (verifyEmail.fulfilled.match(result)) {
        toast.success("Email verified successfully!");
        dispatch(getProfile() as any);
        navigate("/dashboard");
      }
    } catch (error: any) {
      if (!error?.includes("session expired")) {
        toast.error(error || "Verification failed");
      }
    }
  };

  const handleResend = async () => {
    if (countdown > 0) {
      toast.error(
        `Please wait ${countdown} seconds before requesting a new code`,
      );
      return;
    }

    setIsResending(true);
    try {
      await authService.resendVerification(email!);
      toast.success("Verification code sent successfully!");
      setCountdown(60);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || "Failed to resend verification code",
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      const newOtp = [...otp];

      digits.slice(0, 6).forEach((digit, index) => {
        newOtp[index] = digit;
      });

      setOtp(newOtp);

      const lastInput = inputRefs.current[Math.min(5, digits.length - 1)];
      if (lastInput) lastInput.focus();
    } else {
      toast.error("Please paste exactly 6 digits");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Pelivor Recovery" className="h-12 w-auto" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Verify Your Email
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              We sent a 6-digit code to{" "}
              <span className="font-medium text-primary">{email}</span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The code expires in 10 minutes
            </p>
          </div>
        </div>

        {/* Verification Card */}
        <div className={`${GlobalStyles.card} !p-8`}>
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter the verification code sent to your email address
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* OTP Inputs */}
            <div className="space-y-4">
              <label className={`${GlobalStyles.inputLabel} text-center block`}>
                Verification Code
              </label>
              <div
                className="flex justify-center space-x-2 sm:space-x-3"
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`${GlobalStyles.input} w-10 h-12 sm:w-12 text-center text-xl font-semibold !p-0`}
                    autoFocus={index === 0}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                You can paste the 6-digit code
              </p>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || otp.join("").length !== 6}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} w-full ${
                  otp.join("").length !== 6
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}
                    ></div>
                    Verifying...
                  </div>
                ) : (
                  "Verify Email"
                )}
              </button>
            </div>
          </form>

          {/* Resend Code */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResend}
              disabled={isResending || countdown > 0}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${
                countdown > 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isResending ? (
                <>
                  <div className={`${GlobalStyles.spinner} w-4 h-4 mr-2`}></div>
                  Sending...
                </>
              ) : countdown > 0 ? (
                `Resend in ${countdown}s`
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend Code
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className={`${GlobalStyles.card} mt-6 !p-6`}>
          <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            Verification Tips
          </h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Check your spam folder if you don't see the email</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>The code expires in 10 minutes</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>You can request a new code if it expires</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Make sure you entered the correct email address</span>
            </li>
          </ul>
        </div>

        {/* Security Notice */}
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Shield className="h-4 w-4 text-primary" />
          <span>Email verification ensures account security</span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
